import { useQuery } from '@tanstack/react-query';

import { getFileDownloadUrl } from '@/shared/api/assets.api';

// Server-side signed URLs live for 100 minutes (URL_TTL in assets-api).
// Refresh well before then so the URL stays valid for the lifetime of a
// playback session.
const SIGNED_URL_STALE_MS = 90 * 60 * 1000;

/**
 * Returns a signed download URL for the given asset, suitable for passing
 * straight to a `<video>` or `<audio>` element. Unlike `useGetDownloadUrl`,
 * this hook does NOT fetch the asset bytes — the URL is handed to the browser
 * so it can stream via HTTP byte-range requests instead of buffering the full
 * payload into a `blob:` object URL.
 */
export const useGetSignedUrl = (assetId?: string) => {
  const query = useQuery({
    queryKey: ['signedUrl', assetId],
    queryFn: () => {
      if (!assetId) {
        throw new Error('Asset ID is required');
      }
      return getFileDownloadUrl(assetId);
    },
    enabled: !!assetId,
    staleTime: SIGNED_URL_STALE_MS,
    retry: false,
  });

  return {
    downloadUrl: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
  };
};
